/**
 * Quiz Configuration Builder
 *
 * Builds QuizConfig objects from wizard state.
 * Uses simple random IDs instead of content-based hashing.
 */

import type { QuizConfig, StoredFilterRule } from "@/db/types";
import type { FilterRule } from "@/stores/useStudioStore";
import { generateQuizId } from "./idGenerator";

/**
 * Compute dataset signature based on source
 * Simple identifier for the data source
 */
export function computeDatasetSignature(config: {
  datasetSource: QuizConfig["datasetSource"];
  datasetLabel: string;
}): string {
  const { datasetSource, datasetLabel } = config;

  if (datasetSource?.type === "dataset-url") {
    return `dataset-url:${datasetSource.path}`;
  }

  if (datasetSource?.type === "inline") {
    return datasetSource.id;
  }

  return `dataset:${datasetLabel}`;
}

/**
 * Convert wizard FilterRule to StoredFilterRule
 */
function normalizeFilter(filter: FilterRule): StoredFilterRule {
  const normalized: StoredFilterRule = {
    propertyKey: filter.propertyKey,
    mode: filter.mode,
    values: [...filter.values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    ),
  };

  if (filter.mode === "comparison" && filter.operator) {
    normalized.operator = filter.operator;
  }

  if (filter.mode === "ranking") {
    normalized.rankingDirection = filter.rankingDirection;
    normalized.rankingCount = filter.rankingCount;
  }

  return normalized;
}


/**
 * Convert wizard state to QuizConfig format
 */
export function buildQuizConfig(params: {
  title: string;
  description: string;
  category: QuizConfig["category"];
  geography: string; // Country/region name
  datasetLabel: string;
  datasetSource: QuizConfig["datasetSource"];
  promptKeys: string[];
  scopeKey: string | null;
  scopeBias: number;
  repetitions: number;
  maxAttemptsPerPrompt: number;
  filters: FilterRule[];
  excludedFeatureIds: string[];
  totalFeatures: number;
  eligibleCount: number;
  granularityLevel: number;
  storedCollection?: string;
}): QuizConfig {
  // Normalize filters
  const normalizedFilters = params.filters
    .map(normalizeFilter)
    .sort((a, b) => a.propertyKey.localeCompare(b.propertyKey));

  // Compute dataset signature
  const datasetSignature = computeDatasetSignature({
    datasetSource: params.datasetSource,
    datasetLabel: params.datasetLabel,
  });

  // Build config object
  const config: QuizConfig = {
    signature: generateQuizId(), // Simple random ID
    title: params.title.trim(),
    description: params.description.trim(),
    category: params.category,
    geography: params.geography,
    mapQuizId: null,
    mapQuizName: null,
    datasetLabel: params.datasetLabel,
    datasetSignature,
    datasetSource: params.datasetSource,
    promptKeys: [...params.promptKeys].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    ),
    scopeKey: params.scopeKey,
    scopeBias: params.scopeBias,
    repetitions: params.repetitions,
    maxAttemptsPerPrompt: params.maxAttemptsPerPrompt,
    filters: normalizedFilters,
    excludedFeatureIds: [...params.excludedFeatureIds].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    ),
    eligibleCount: params.eligibleCount,
    totalFeatures: params.totalFeatures,
    granularityLevel: params.granularityLevel,
    storedCollection: params.storedCollection,
    isPublished: false,
    createdAt: new Date().toISOString(),
    lastUsedAt: new Date().toISOString(),
  };

  return config;
}
